import { useEffect, useState } from 'react';
import { useLocale } from '../../lib/locale';
import { Search, MapPin, Star, Filter, SlidersHorizontal, Heart, Sparkles, TrendingUp, ChevronRight, Music, Wine, Utensils, Coffee, Users, Award, Tag } from 'lucide-react';
import type { User as UserType, Restaurant } from '../../App';
import { api } from '../../lib/api';
import { resolveAssetUrl } from '../../lib/assets';
import { DineOutFiltersModal, DineOutFilters } from './DineOutFiltersModal';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { RestaurantDetailsView } from './RestaurantDetailsView';
import { LocationSelector } from './LocationSelector';
import { ProfileDropdown } from './ProfileDropdown';
import { useTheme } from '../../contexts/ThemeContext';
import { ToastContainer, useToast } from '../ui/Toast';

interface DineOutViewProps {
  user: UserType;
  onLogout: () => void;
  onBackToModeSelection?: () => void;
  currentLocation: string;
  onLocationChange: (location: string) => void;
}

type RestaurantFilter = 'all' | 'fine-dining' | 'cafes' | 'family' | 'rooftop' | 'bars' | 'pure-veg';

const normalizeRestaurantTime = (value: unknown, fallback: string): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const matched = raw.match(/(\d{2}):(\d{2})(?::\d{2})?/);
  return matched ? `${matched[1]}:${matched[2]}` : fallback;
};

interface DineOutRestaurant extends Restaurant {
  ambiance: string[];
  priceForTwo: number;
  dineOutOffer?: string;
  highlights: string[];
  category: RestaurantFilter;
  cuisineType: string[];
  seatingTypes: string[];
  dietaryOptions: string[];
  distanceKm: number;
}

export function DineOutView({ user, onLogout, onBackToModeSelection, currentLocation, onLocationChange }: DineOutViewProps) {
  const { formatMoney } = useLocale();
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === 'dark';
  const { toasts, removeToast, warning } = useToast();
  const FAVORITES_STORAGE_KEY = 'customer_favorite_restaurants';
  const goToLandingPage = () => {
    onBackToModeSelection?.();
    if (window.location.pathname === '/') return;
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [selectedFilter, setSelectedFilter] = useState<RestaurantFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dineOutRestaurants, setDineOutRestaurants] = useState<DineOutRestaurant[]>([]);
  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [restaurantsError, setRestaurantsError] = useState('');
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(parsed) ? parsed.map((id: any) => String(id)) : []);
    } catch {
      return new Set();
    }
  });
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [activeFilters, setActiveFilters] = useState<DineOutFilters>({
    distance: [],
    areas: [],
    cuisines: [],
    priceRanges: [],
    minRating: null,
    openNow: false,
    openAtTime: '',
    diningExperience: [],
    seatingType: [],
    occasions: [],
    offers: [],
    tableBooking: [],
    dietaryPreferences: [],
    alcohol: [],
    popularity: [],
    sortBy: 'relevance',
    amenities: [],
    paymentOptions: [],
    crowdLevel: [],
    safetyHygiene: []
  });

  useEffect(() => {
    if (!selectedRestaurant) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [selectedRestaurant]);

  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(Array.from(favorites)));
    } catch {
      // ignore
    }
  }, [favorites]);

  useEffect(() => {
    let mounted = true;

    const parseCuisineList = (value: any): string[] =>
      (Array.isArray(value)
        ? value
        : String(value || '')
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean)
      )
        .map((item: any) => String(item || '').trim())
        .filter(Boolean);

    const getStoredCoordinates = (): { latitude: number; longitude: number } | null => {
      const latRaw = localStorage.getItem('customer_latitude');
      const lngRaw = localStorage.getItem('customer_longitude');
      if (!latRaw || !lngRaw) return null;
      const latitude = Number(latRaw);
      const longitude = Number(lngRaw);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
      return { latitude, longitude };
    };

    const loadRestaurants = async () => {
      try {
        if (mounted) {
          setRestaurantsLoading(true);
          setRestaurantsError('');
        }

        const userCoords = getStoredCoordinates();
        const response = userCoords
          ? await api.restaurants.getNearby({
              latitude: userCoords.latitude,
              longitude: userCoords.longitude,
              radiusKm: 25,
              limit: 100,
            } as any)
          : await api.restaurants.getAll({ page: 1, limit: 100 } as any);

        const rawRows = Array.isArray((response as any)?.data)
          ? (response as any).data
          : Array.isArray((response as any)?.data?.restaurants)
          ? (response as any).data.restaurants
          : [];

        const mapped: DineOutRestaurant[] = rawRows
          .map((row: any, index: number) => {
            const openTime = normalizeRestaurantTime(row?.openTime || row?.open_time, '09:00');
            const closeTime = normalizeRestaurantTime(row?.closeTime || row?.close_time, '22:00');
            const deliveryRaw = String(row?.deliveryTime || row?.delivery_time || '30-45').trim();
            const minimumOrder = Number(row?.minimumOrder ?? row?.minimum_order ?? 0) || 0;
            const deliveryFee = Number(row?.deliveryFee ?? row?.delivery_fee ?? 0) || 0;
            const packagingCharges = Number(row?.packagingCharges ?? row?.packaging_charges ?? 0) || 0;
            const rating = Number(row?.rating ?? 0) || 0;
            const isActive = Boolean(row?.isActive ?? row?.is_active ?? true);
            const imageCandidate = String(row?.coverImage || row?.cover_image || row?.banner || row?.logo || '').trim();
            const imageUrl =
              resolveAssetUrl(imageCandidate) ||
              imageCandidate ||
              'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200';

            const resolvedDistance =
              row?.distance !== undefined && row?.distance !== null
                ? Number(row.distance)
                : Number((1.2 + (index % 8) * 0.6).toFixed(1));

            const baseRestaurant: Restaurant = {
              id: String(row?.id || `dynamic-${index}`),
              name: String(row?.name || 'Restaurant'),
              description: String(row?.description || 'Dine-in experiences made easy'),
              logo: resolveAssetUrl(String(row?.logo || '').trim()) || String(row?.logo || imageUrl),
              banner: imageUrl,
              coverImage: imageUrl,
              address: String(row?.address || ''),
              city: String(row?.city || 'City'),
              phone: String(row?.phone || ''),
              email: String(row?.email || ''),
              primaryColor: '#2563eb',
              secondaryColor: '#eff6ff',
              isActive,
              rating: rating > 0 ? rating : 0,
              totalOrders: Number(row?.totalOrders || 0),
              totalRatings: Number(row?.totalReviews || 0),
              cuisine: parseCuisineList(row?.cuisine || row?.cuisineType),
              openTime,
              closeTime,
              deliveryTime: deliveryRaw.includes('min') ? deliveryRaw : `${deliveryRaw} mins`,
              minimumOrder,
              deliveryFee,
              freeDeliveryAbove: minimumOrder > 0 ? minimumOrder * 2 : 500,
              taxRate: 5,
              packagingCharges,
              distance: Number.isFinite(resolvedDistance) ? Number(resolvedDistance.toFixed(1)) : undefined,
              isPureVeg: Boolean(row?.isPureVeg || row?.is_pure_veg || false),
              isOpen: (() => {
                const raw = row?.isOpen ?? row?.is_open;
                if (raw === false || raw === 0 || raw === '0' || String(raw).toLowerCase() === 'false') return false;
                if (raw === true || raw === 1 || raw === '1' || String(raw).toLowerCase() === 'true') return true;
                return isActive;
              })(),
              hasOffers: Boolean(row?.hasOffers || row?.has_offers || false),
              amenities: Array.isArray(row?.amenities) ? row.amenities : [],
            };

            const category: RestaurantFilter = baseRestaurant.isPureVeg ? 'pure-veg' : 'family';
            const distanceKm = baseRestaurant.distance ? Number(baseRestaurant.distance) : 0;

            return {
              ...baseRestaurant,
              ambiance: [],
              priceForTwo: Math.max(500, minimumOrder ? minimumOrder * 3 : 800),
              dineOutOffer: undefined,
              highlights: [],
              category,
              cuisineType: baseRestaurant.cuisine,
              seatingTypes: [],
              dietaryOptions: baseRestaurant.isPureVeg ? ['Vegetarian'] : [],
              distanceKm,
            };
          })
          .filter((row: DineOutRestaurant) => row.isActive);

        if (mounted) setDineOutRestaurants(mapped);
      } catch (error: any) {
        if (mounted) {
          setDineOutRestaurants([]);
          setRestaurantsError(error?.message || 'Unable to load restaurants right now.');
        }
      } finally {
        if (mounted) setRestaurantsLoading(false);
      }
    };

    void loadRestaurants();
    return () => {
      mounted = false;
    };
  }, []);

  const filters: { id: RestaurantFilter; label: string; icon: any }[] = [
    { id: 'all', label: 'All', icon: Utensils },
    { id: 'fine-dining', label: 'Fine Dining', icon: Award },
    { id: 'cafes', label: 'Cafes', icon: Coffee },
    { id: 'family', label: 'Family Restaurants', icon: Users },
    { id: 'rooftop', label: 'Rooftop', icon: Sparkles },
    { id: 'bars', label: 'Bars', icon: Wine },
    { id: 'pure-veg', label: 'Pure Veg', icon: Utensils }
  ];

  const toggleFavorite = (restaurantId: string) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(restaurantId)) {
      newFavorites.delete(restaurantId);
    } else {
      newFavorites.add(restaurantId);
    }
    setFavorites(newFavorites);
  };

  const filteredRestaurants = dineOutRestaurants.filter(restaurant => {
    if (selectedFilter !== 'all' && restaurant.category !== selectedFilter) {
      return false;
    }

    if (searchQuery && !restaurant.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !restaurant.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    if (activeFilters.distance.length > 0) {
      const hasMatchingDistance = activeFilters.distance.some(d => {
        if (d === 'Near Me' && restaurant.distanceKm <= 2) return true;
        if (d === 'Within 1 km' && restaurant.distanceKm <= 1) return true;
        if (d === 'Within 3 km' && restaurant.distanceKm <= 3) return true;
        if (d === 'Within 5 km' && restaurant.distanceKm <= 5) return true;
        return false;
      });
      if (!hasMatchingDistance) return false;
    }

    if (activeFilters.cuisines.length > 0) {
      const hasCuisine = activeFilters.cuisines.some(c =>
        restaurant.cuisineType.some(ct => ct.toLowerCase().includes(c.toLowerCase()))
      );
      if (!hasCuisine) return false;
    }

    if (activeFilters.priceRanges.length > 0) {
      const hasMatchingPrice = activeFilters.priceRanges.some(range => {
        if (range === '0-500' && restaurant.priceForTwo <= 500) return true;
        if (range === '500-1000' && restaurant.priceForTwo > 500 && restaurant.priceForTwo <= 1000) return true;
        if (range === '1000-2000' && restaurant.priceForTwo > 1000 && restaurant.priceForTwo <= 2000) return true;
        if (range === '2000+' && restaurant.priceForTwo > 2000) return true;
        return false;
      });
      if (!hasMatchingPrice) return false;
    }

    if (activeFilters.minRating !== null && restaurant.rating < activeFilters.minRating) {
      return false;
    }

    if (activeFilters.diningExperience.length > 0) {
      const hasExperience = activeFilters.diningExperience.some(exp => {
        if (exp === 'Fine Dining' && restaurant.category === 'fine-dining') return true;
        if (exp === 'Cafe' && restaurant.category === 'cafes') return true;
        if (exp === 'Rooftop' && restaurant.category === 'rooftop') return true;
        if (exp === 'Family Restaurant' && restaurant.category === 'family') return true;
        return false;
      });
      if (!hasExperience) return false;
    }

    if (activeFilters.seatingType.length > 0) {
      const hasSeating = activeFilters.seatingType.some(s =>
        restaurant.seatingTypes.some(st => st.includes(s))
      );
      if (!hasSeating) return false;
    }

    if (activeFilters.amenities.length > 0) {
      const hasAmenity = activeFilters.amenities.some(a =>
        (restaurant.amenities ?? []).some(am => am.toLowerCase().includes(a.toLowerCase().replace(/[^\w\s]/g, '')))
      );
      if (!hasAmenity) return false;
    }

    if (activeFilters.dietaryPreferences.length > 0) {
      const hasDietary = activeFilters.dietaryPreferences.some(d => {
        if (d === 'Pure Veg' && restaurant.category === 'pure-veg') return true;
        return restaurant.dietaryOptions.some(opt => opt.toLowerCase().includes(d.toLowerCase()));
      });
      if (!hasDietary) return false;
    }

    if (activeFilters.offers.length > 0 && !restaurant.dineOutOffer) {
      return false;
    }

    return true;
  });

  const sortedRestaurants = [...filteredRestaurants].sort((a, b) => {
    switch (activeFilters.sortBy) {
      case 'rating-desc':
        return b.rating - a.rating;
      case 'price-asc':
        return a.priceForTwo - b.priceForTwo;
      case 'distance':
        return a.distanceKm - b.distanceKm;
      case 'popularity':
        return (b.totalRatings ?? 0) - (a.totalRatings ?? 0);
      default:
        return 0;
    }
  });

  return (
    <div className="customer-theme min-h-screen bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2344b6] to-[#3f83f8] text-white">
        <div className="px-3 py-3 sm:px-4 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={goToLandingPage}
              className="flex items-center gap-2 text-white hover:text-white/95 transition-colors"
              title="Go to Landing Page"
            >
              <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FC8019] to-[#E84545] flex items-center justify-center shadow-lg shadow-orange-500/20">
                <Utensils className="w-5 h-5 text-white" />
              </span>
              <span className="text-lg font-extrabold tracking-tight">FoodHub</span>
            </button>
            <div className="flex items-center gap-2 sm:gap-3 flex-nowrap justify-end min-w-0 overflow-hidden">
              <div className="hidden sm:block">
                <LocationSelector currentLocation={currentLocation} onLocationChange={onLocationChange} variant="header" />
              </div>
              <ProfileDropdown user={user} onLogout={onLogout} variant="compact" />
            </div>
          </div>

          <div className="pt-3 flex justify-end sm:hidden">
            <LocationSelector currentLocation={currentLocation} onLocationChange={onLocationChange} variant="header" />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-[#5aa0f6] via-[#4b8ff0] to-[#3b82f6] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-10 sm:py-14 lg:py-16">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-5 sm:mb-6">
              <Sparkles className="w-5 h-5 text-[#FB923C]" />
              <span className="text-sm font-medium">Discover Amazing Dining Experiences</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl leading-tight mb-3 sm:mb-4">Dine Out Restaurants Near You</h1>
            <p className="text-base sm:text-lg md:text-xl text-blue-100/95 mb-6 sm:mb-8 max-w-3xl mx-auto">
              Explore the finest dining destinations, from cozy cafes to elegant fine dining
            </p>

            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 sm:left-5 top-1/2 transform -translate-y-1/2 text-[#60A5FA] w-5 h-5 sm:w-6 sm:h-6" />
                <input
                  type="text"
                  placeholder="Search for restaurants, cuisines, or dishes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 sm:pl-16 pr-4 sm:pr-6 py-4 sm:py-5 rounded-2xl bg-white text-gray-900 shadow-2xl focus:outline-none focus:ring-4 focus:ring-white/50 text-base sm:text-lg border-2 border-white/20 hover:border-white/40 transition-all placeholder:text-gray-400"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          <div className="flex sm:justify-center overflow-x-auto gap-2.5 sm:gap-3 no-scrollbar pb-1">
            {filters.map(filter => {
              const Icon = filter.icon;
              return (
                <button
                    key={filter.id}
                    onClick={() => setSelectedFilter(filter.id)}
                    className={`px-4 sm:px-5 py-2.5 rounded-full whitespace-nowrap font-medium transition-all flex items-center gap-2 ${
                      selectedFilter === filter.id
                      ? 'bg-[#60A5FA] text-white shadow-lg shadow-blue-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl text-gray-900 mb-1">
              {selectedFilter === 'all' ? 'All Restaurants' : filters.find(f => f.id === selectedFilter)?.label}
            </h2>
            <p className="text-gray-600">
              {restaurantsLoading ? 'Loading restaurants...' : `${filteredRestaurants.length} restaurants found`}
            </p>
            {restaurantsError && !restaurantsLoading && (
              <p className="text-sm text-red-600 mt-2">{restaurantsError}</p>
            )}
          </div>
          <button
            className="w-full sm:w-auto justify-center flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
            onClick={() => setShowFiltersModal(true)}
          >
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">More Filters</span>
          </button>
        </div>

        {restaurantsLoading ? (
          <div className="text-center py-16 text-gray-600">Loading restaurants...</div>
        ) : restaurantsError ? (
          <div className="text-center py-16">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl text-gray-600 mb-2">Unable to load restaurants</h3>
            <p className="text-gray-500">Please try again in a moment.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6">
              {sortedRestaurants.map((restaurant) => (
                <RestaurantCard
                  key={restaurant.id}
                  restaurant={restaurant}
                  isFavorite={favorites.has(restaurant.id)}
                  onToggleFavorite={() => toggleFavorite(restaurant.id)}
                  isClosed={
                    restaurant.isOpen === false ||
                    (restaurant as any)?.is_open === 0 ||
                    (restaurant as any)?.is_open === '0' ||
                    String((restaurant as any)?.is_open ?? '').toLowerCase() === 'false'
                  }
                  onSelect={() => {
                    const isClosed =
                      restaurant.isOpen === false ||
                      (restaurant as any)?.is_open === 0 ||
                      (restaurant as any)?.is_open === '0' ||
                      String((restaurant as any)?.is_open ?? '').toLowerCase() === 'false';
                    if (isClosed) {
                      warning('Restaurant is closed.');
                      return;
                    }
                    setSelectedRestaurant(restaurant);
                  }}
                />
              ))}
            </div>

            {filteredRestaurants.length === 0 && (
              <div className="text-center py-16">
                <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl text-gray-600 mb-2">No restaurants found</h3>
                <p className="text-gray-500">Try adjusting your search or filters</p>
              </div>
            )}
          </>
        )}
      </div>

      <div className={isDarkMode ? 'bg-gradient-to-b from-slate-900 via-slate-950 to-[#020617]' : 'bg-gradient-to-b from-blue-50/60 to-transparent'}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-10 sm:py-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-2xl sm:text-3xl mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Curated Collections</h2>
              <p className={isDarkMode ? 'text-slate-300' : 'text-gray-600'}>Handpicked dining experiences just for you</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
            <CollectionCard
              title="Romantic Dining"
              count={12}
              icon={<Heart className="w-6 h-6" />}
              color="from-pink-500 to-red-500"
              isDarkMode={isDarkMode}
            />
            <CollectionCard
              title="Live Music Venues"
              count={8}
              icon={<Music className="w-6 h-6" />}
              color="from-purple-500 to-indigo-500"
              isDarkMode={isDarkMode}
            />
            <CollectionCard
              title="Best Rooftop Bars"
              count={15}
              icon={<Wine className="w-6 h-6" />}
              color="from-[#FB923C] to-amber-500"
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      </div>

      {selectedRestaurant && (
        <div className="fixed inset-0 bg-white z-50 overflow-y-auto">
          <RestaurantDetailsView
            restaurant={selectedRestaurant}
            onBack={() => setSelectedRestaurant(null)}
            user={user}
          />
        </div>
      )}

      {showFiltersModal && (
        <DineOutFiltersModal
          onClose={() => setShowFiltersModal(false)}
          initialFilters={activeFilters}
          onApplyFilters={setActiveFilters}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}

function RestaurantCard({
  restaurant,
  isFavorite,
  onToggleFavorite,
  isClosed,
  onSelect
}: {
  restaurant: DineOutRestaurant;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  isClosed: boolean;
  onSelect: () => void;
}) {
  const { formatMoney } = useLocale();

  return (
    <div
      className={`bg-white rounded-2xl shadow-sm transition-all duration-300 overflow-hidden group border border-slate-200/80 ${
        isClosed ? 'opacity-60 grayscale cursor-not-allowed' : 'cursor-pointer hover:shadow-2xl hover:-translate-y-1'
      }`}
    >
      <div className="relative h-48 sm:h-52 overflow-hidden">
        <ImageWithFallback
          src={restaurant.coverImage || restaurant.logo}
          alt={restaurant.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />

        <div className="absolute top-3 left-3 z-10 flex flex-col gap-2 max-w-[85%]">
          <div
            className={`px-3 py-1.5 rounded-full text-xs font-bold shadow-lg ${
              isClosed ? 'bg-red-600 text-white' : 'bg-green-600 text-white'
            }`}
          >
            {isClosed ? 'Offline' : 'Online'}
          </div>

          {restaurant.dineOutOffer && (
            <div className="bg-gradient-to-r from-[#FB923C] to-[#F97316] text-white px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" />
              <span className="text-xs font-bold truncate">{restaurant.dineOutOffer}</span>
            </div>
          )}
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            if (!isClosed) onToggleFavorite();
          }}
          disabled={isClosed}
          className={`absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors shadow-lg ${
            isClosed ? 'opacity-70 cursor-not-allowed' : 'hover:bg-white'
          }`}
        >
          <Heart
            className={`w-5 h-5 transition-colors ${
              isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'
            }`}
          />
        </button>

        <div className="absolute bottom-3 left-3">
          <div className="bg-[#4ADE80] text-white px-2.5 py-1 rounded-lg shadow-lg flex items-center gap-1">
            <Star className="w-3.5 h-3.5 fill-white" />
            <span className="text-sm font-bold">{restaurant.rating}</span>
            <span className="text-xs opacity-90">({restaurant.totalRatings ?? 0})</span>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        <h3 className="text-xl text-gray-900 mb-2 group-hover:text-[#60A5FA] transition-colors truncate">
          {restaurant.name}
        </h3>

        <p className="text-sm text-gray-600 mb-3">{restaurant.description}</p>

        <div className="flex items-center gap-2 text-gray-600 mb-4">
          <MapPin className="w-4 h-4" />
          <span className="text-sm truncate">{restaurant.address.split(',')[0]} - {restaurant.distanceKm.toFixed(1)} km</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {restaurant.ambiance.slice(0, 2).map((highlight, index) => (
            <span
              key={index}
              className="text-xs bg-blue-50 text-[#60A5FA] px-3 py-1 rounded-full font-medium"
            >
              {highlight}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100 gap-3">
          <div>
            <span className="text-sm text-gray-500">For two</span>
            <p className="text-lg text-gray-900">{formatMoney(restaurant.priceForTwo)}</p>
          </div>
          <button
            disabled={isClosed}
            className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-lg transition-colors font-medium shadow-lg shadow-blue-200/50 ${
              isClosed
                ? 'bg-slate-200 text-slate-600 cursor-not-allowed'
                : 'bg-[#60A5FA] text-white hover:bg-[#3B82F6] group-hover:shadow-xl'
            }`}
            onClick={onSelect}
          >
            <span>View Details</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function CollectionCard({
  title,
  count,
  icon,
  color,
  isDarkMode,
}: {
  title: string;
  count: number;
  icon: React.ReactNode;
  color: string;
  isDarkMode: boolean;
}) {
  return (
    <div className={`group relative rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer hover:-translate-y-1 ${
      isDarkMode
        ? 'bg-slate-900/95 border border-slate-700/70'
        : 'bg-white border border-slate-200/80'
    }`}>
      <div className={`absolute inset-0 bg-gradient-to-br ${color} ${isDarkMode ? 'opacity-5 group-hover:opacity-15' : 'opacity-0 group-hover:opacity-10'} transition-opacity duration-300`}></div>
      <div className="relative p-6 sm:p-8">
        <div className={`w-14 h-14 bg-gradient-to-br ${color} rounded-2xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
          <div className="text-white">
            {icon}
          </div>
        </div>
        <h3 className={`text-xl mb-2 group-hover:text-[#60A5FA] transition-colors ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {title}
        </h3>
        <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>{count} places</p>
        <div className="flex items-center text-[#60A5FA] font-medium group-hover:gap-3 transition-all">
          <span className="text-sm">Explore Collection</span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}
